const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const buildPercentFromShot = (percent, made, attempted) => {
    const parsedPercent = Number(percent);
    if (Number.isFinite(parsedPercent)) {
        return Math.max(0, Math.min(100, Math.round(parsedPercent)));
    }

    const attemptedValue = toNumber(attempted);
    if (!attemptedValue) {
        return 0;
    }

    return Math.max(0, Math.min(100, Math.round((toNumber(made) / attemptedValue) * 100)));
};

function ComparisonMetricCard({
    title,
    leftValue,
    rightValue,
    leftColorClass,
    rightColorClass,
    leftCode,
    rightCode,
}) {
    const maxValue = Math.max(leftValue, rightValue, 1);
    const leftHeight = (leftValue / maxValue) * 100;
    const rightHeight = (rightValue / maxValue) * 100;

    return (
        <article className="h-full rounded-xl border border-white/10 bg-[#0f1f15] p-3 sm:p-4 flex flex-col">
            <h4 className="h-10 sm:h-16 flex items-center justify-center text-center text-slate-100 text-xs sm:text-sm md:text-base font-black uppercase tracking-tight leading-tight mb-3 sm:mb-4">{title}</h4>

            <div className="grid grid-cols-2 gap-2 sm:gap-3 text-center">
                <span className={`${leftColorClass.replace("bg-", "text-")} text-[10px] sm:text-xs font-black uppercase tracking-wider truncate`}>{leftCode}</span>
                <span className={`${rightColorClass.replace("bg-", "text-")} text-[10px] sm:text-xs font-black uppercase tracking-wider truncate`}>{rightCode}</span>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:gap-3 items-end h-28 sm:h-40 mt-1.5 sm:mt-2">
                <div className="h-full rounded bg-white/5 flex items-end overflow-hidden">
                    <div className={`w-full ${leftColorClass}`} style={{ height: `${leftHeight}%` }} />
                </div>
                <div className="h-full rounded bg-white/5 flex items-end overflow-hidden">
                    <div className={`w-full ${rightColorClass}`} style={{ height: `${rightHeight}%` }} />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-2 sm:mt-3 text-center">
                <span className="inline-flex items-center justify-center h-7 sm:h-8 bg-[#0b1b12] text-base sm:text-xl font-black text-slate-100 tabular-nums rounded">
                    {leftValue}
                </span>
                <span className="inline-flex items-center justify-center h-7 sm:h-8 bg-[#0b1b12] text-base sm:text-xl font-black text-slate-100 tabular-nums rounded">
                    {rightValue}
                </span>
            </div>
        </article>
    );
}

function ShotPercentRow({ label, percent, made, attempted, accentClass }) {
    const normalizedPercent = Math.max(0, Math.min(100, toNumber(percent)));
    const ringColor = accentClass === "text-green-400" ? "#22c55e" : "#ef4444";

    return (
        <div className="flex items-center justify-between gap-2 sm:gap-4">
            <p className="w-[60px] sm:w-[84px] text-left text-slate-400 text-lg sm:text-2xl font-black uppercase tracking-[0.04em]">{label}</p>

            <div
                className="relative w-16 h-16 sm:w-24 sm:h-24 rounded-full flex items-center justify-center shrink-0"
                style={{
                    background: `conic-gradient(${ringColor} ${normalizedPercent}%, rgba(255,255,255,0.10) ${normalizedPercent}% 100%)`,
                }}
            >
                <div className="w-[46px] h-[46px] sm:w-[68px] sm:h-[68px] rounded-full bg-[#102216] flex items-center justify-center">
                    <span className="text-slate-100 text-sm sm:text-xl font-black tabular-nums">{normalizedPercent}%</span>
                </div>
            </div>

            <p className="text-slate-100 text-lg sm:text-2xl font-black tabular-nums min-w-[60px] sm:min-w-[82px] text-right">{made}/{attempted}</p>
        </div>
    );
}

function TeamShotCard({ title, accentClass, totals }) {
    const twoMade = toNumber(totals?.totalDoblesHechos);
    const twoAttempt = toNumber(totals?.totalDoblesIntentados ?? totals?.totalDoblesIntent);
    const threeMade = toNumber(totals?.totalTriplesHechos);
    const threeAttempt = toNumber(totals?.totalTriplesIntentados ?? totals?.totalTriplesIntent);
    const oneMade = toNumber(totals?.totalLibresHechos);
    const oneAttempt = toNumber(totals?.totalLibresIntentados ?? totals?.totalLibresIntent);

    const twoPercent = buildPercentFromShot(totals?.totalDoblesPercent, twoMade, twoAttempt);
    const threePercent = buildPercentFromShot(totals?.totalTriplesPercent, threeMade, threeAttempt);
    const onePercent = buildPercentFromShot(totals?.totalLibresPercent, oneMade, oneAttempt);

    return (
        <article className="rounded-xl border border-white/10 bg-[#0f1f15] p-3 sm:p-5 h-full">
            <h4 className="text-center text-slate-100 text-lg sm:text-2xl font-black uppercase tracking-tight mb-3 sm:mb-5">{title}</h4>

            <div className="space-y-4 sm:space-y-6">
                <ShotPercentRow label="2PTS" percent={twoPercent} made={twoMade} attempted={twoAttempt} accentClass={accentClass} />
                <ShotPercentRow label="3PTS" percent={threePercent} made={threeMade} attempted={threeAttempt} accentClass={accentClass} />
                <ShotPercentRow label="1PT" percent={onePercent} made={oneMade} attempted={oneAttempt} accentClass={accentClass} />
            </div>

        </article>
    );
}

function PartidoTrendsPanel({
    trends,
    trendsComparison = [],
    aguadaTotals = {},
    rivalTotals = {},
    aguadaName = "Aguada",
    aguadaCode = "AGU",
    rivalName = "Rival",
    rivalCode = "RIV",
    aguadaWon = true,
    isHomeAguada = true,
    showOnlyTrends = false,
}) {
    const leadChanges = trends?.leadChanges || 0;
    const tiedTimes = trends?.tiedTimes || 0;
    const maxAguadaRun = trends?.maxAguadaRun || 0;
    const maxRivalRun = trends?.maxRivalRun || 0;
    const maxLeadAguada = trends?.maxLeadAguada || 0;
    const maxLeadRival = trends?.maxLeadRival || 0;

    const aguadaAccentClass = aguadaWon ? "text-green-400" : "text-red-400";
    const rivalAccentClass = aguadaWon ? "text-red-400" : "text-green-400";
    const aguadaBarColorClass = aguadaWon ? "bg-green-500" : "bg-red-500";
    const rivalBarColorClass = aguadaWon ? "bg-red-500" : "bg-green-500";
    const maxLeadTotal = Math.max(maxLeadAguada + maxLeadRival, 1);
    const aguadaLeadPercent = (maxLeadAguada / maxLeadTotal) * 100;
    const rivalLeadPercent = (maxLeadRival / maxLeadTotal) * 100;
    const showTrendsSection = trends?.canCalculate !== false;

    // Local/visitante determinan el orden de columnas; el color sigue ligado a Aguada/rival según quién ganó.
    const localTeam = isHomeAguada
        ? { name: aguadaName, code: aguadaCode, accentClass: aguadaAccentClass, barColorClass: aguadaBarColorClass, totals: aguadaTotals }
        : { name: rivalName, code: rivalCode, accentClass: rivalAccentClass, barColorClass: rivalBarColorClass, totals: rivalTotals };
    const awayTeam = isHomeAguada
        ? { name: rivalName, code: rivalCode, accentClass: rivalAccentClass, barColorClass: rivalBarColorClass, totals: rivalTotals }
        : { name: aguadaName, code: aguadaCode, accentClass: aguadaAccentClass, barColorClass: aguadaBarColorClass, totals: aguadaTotals };

    const comparisonByKey = trendsComparison.reduce((accumulator, item) => {
        accumulator[item.key] = item;
        return accumulator;
    }, {});

    const hasNodeComparison = (nodeKey) => (
        aguadaTotals?.[nodeKey] !== undefined &&
        aguadaTotals?.[nodeKey] !== null &&
        rivalTotals?.[nodeKey] !== undefined &&
        rivalTotals?.[nodeKey] !== null
    );

    const cards = [
        {
            title: "REBOTES",
            aguada: toNumber(aguadaTotals?.totalRebotesTot),
            rival: toNumber(rivalTotals?.totalRebotesTot),
        },
        {
            title: "ASISTENCIAS",
            aguada: toNumber(aguadaTotals?.totalAsistencias),
            rival: toNumber(rivalTotals?.totalAsistencias),
        },
        {
            title: "RECUPEROS",
            aguada: toNumber(aguadaTotals?.totalRobos),
            rival: toNumber(rivalTotals?.totalRobos),
        },
        {
            title: "PÉRDIDAS",
            aguada: toNumber(aguadaTotals?.totalPerdidas),
            rival: toNumber(rivalTotals?.totalPerdidas),
        },
        {
            title: "PUNTOS DE PÉRDIDAS",
            aguada: toNumber(comparisonByKey?.puntosDePerdida?.aguada),
            rival: toNumber(comparisonByKey?.puntosDePerdida?.rival),
            shouldDisplay: hasNodeComparison("puntosDePerdida"),
        },
        {
            title: "PUNTOS DE CONTRA ATAQUE",
            aguada: toNumber(comparisonByKey?.puntosContraataque?.aguada),
            rival: toNumber(comparisonByKey?.puntosContraataque?.rival),
            shouldDisplay: hasNodeComparison("puntosContraataque"),
        },
        {
            title: "PUNTOS DE SEGUNDA OPORTUNIDAD",
            aguada: toNumber(comparisonByKey?.puntosSegundaOport?.aguada),
            rival: toNumber(comparisonByKey?.puntosSegundaOport?.rival),
            shouldDisplay: hasNodeComparison("puntosSegundaOport"),
        },
        {
            title: "PUNTOS DE LA BANCA",
            aguada: toNumber(comparisonByKey?.puntosBanca?.aguada),
            rival: toNumber(comparisonByKey?.puntosBanca?.rival),
            shouldDisplay: hasNodeComparison("puntosBanca"),
        },
    ].map((card) => ({
        ...card,
        left: isHomeAguada ? card.aguada : card.rival,
        right: isHomeAguada ? card.rival : card.aguada,
    }));

    return (
        <section className="w-full bg-[#102216] rounded-xl p-3 sm:p-4 md:p-6 border border-white/10 space-y-4 sm:space-y-6">
            <div className={`gap-4 sm:gap-6 items-stretch ${showTrendsSection && !showOnlyTrends ? "grid grid-cols-1 xl:grid-cols-[280px_1fr]" : ""}`}>
                {showTrendsSection ? (
                    <aside className="w-full rounded-xl border border-white/10 bg-[#0f1f15] p-4 sm:p-5">
                        <h3 className="text-xl sm:text-2xl font-bold text-slate-100 mb-4 sm:mb-5 flex items-center gap-2">
                            <span className="w-2 h-8 bg-green-500 rounded-full" />
                            Tendencias
                        </h3>

                        <div className="space-y-3 sm:space-y-4">
                            <div className="pb-3 sm:pb-4 border-b border-white/10 flex items-center justify-between gap-3">
                                <span className="text-slate-400 text-[11px] sm:text-xs uppercase tracking-[0.14em] font-bold">Cambios liderazgo</span>
                                <span className="text-slate-100 text-xl sm:text-2xl font-black tabular-nums">{leadChanges}</span>
                            </div>

                            <div className="pb-3 sm:pb-4 border-b border-white/10 flex items-center justify-between gap-3">
                                <span className="text-slate-400 text-[11px] sm:text-xs uppercase tracking-[0.14em] font-bold">Veces empatados</span>
                                <span className="text-slate-100 text-xl sm:text-2xl font-black tabular-nums">{tiedTimes}</span>
                            </div>

                            <div className="pb-3 sm:pb-4 border-b border-white/10 flex items-center justify-between gap-3">
                                <span className="text-slate-400 text-[11px] sm:text-xs uppercase tracking-[0.14em] font-bold">Racha máxima {aguadaCode}</span>
                                <span className={`${aguadaAccentClass} text-xl sm:text-2xl font-black tabular-nums`}>{maxAguadaRun}-0</span>
                            </div>

                            <div className="pb-3 sm:pb-4 border-b border-white/10 flex items-center justify-between gap-3">
                                <span className="text-slate-400 text-[11px] sm:text-xs uppercase tracking-[0.14em] font-bold">Racha máxima {rivalCode}</span>
                                <span className={`${rivalAccentClass} text-xl sm:text-2xl font-black tabular-nums`}>{maxRivalRun}-0</span>
                            </div>

                            <div>
                                <p className="text-slate-400 text-xs uppercase tracking-[0.14em] font-bold mb-3">Máxima ventaja</p>
                                <div className="h-2 rounded-full overflow-hidden flex bg-[#0b1b12] border border-white/10">
                                    <div className={aguadaBarColorClass} style={{ width: `${aguadaLeadPercent}%` }} />
                                    <div className={rivalBarColorClass} style={{ width: `${rivalLeadPercent}%` }} />
                                </div>

                                <div className="flex items-center justify-between mt-2 text-sm font-bold">
                                    <span className={aguadaAccentClass}>+{maxLeadAguada} {aguadaCode}</span>
                                    <span className={rivalAccentClass}>+{maxLeadRival} {rivalCode}</span>
                                </div>
                            </div>
                        </div>
                    </aside>
                ) : null}

                {!showOnlyTrends ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <TeamShotCard
                            title={localTeam.name}
                            accentClass={localTeam.accentClass}
                            totals={localTeam.totals}
                        />
                        <TeamShotCard
                            title={awayTeam.name}
                            accentClass={awayTeam.accentClass}
                            totals={awayTeam.totals}
                        />
                    </div>
                ) : null}
            </div>

            {!showOnlyTrends ? (
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                    {cards
                        .filter((card) => card.shouldDisplay !== false)
                        .map((card) => (
                            <ComparisonMetricCard
                                key={card.title}
                                title={card.title}
                                leftValue={card.left}
                                rightValue={card.right}
                                leftColorClass={localTeam.barColorClass}
                                rightColorClass={awayTeam.barColorClass}
                                leftCode={localTeam.code}
                                rightCode={awayTeam.code}
                            />
                        ))}
                </div>
            ) : null}
        </section>
    );
}

export default PartidoTrendsPanel;
